import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, API_ENDPOINTS, useAuthStore, PLAN_TRANSACTION_FEES } from '@dukene/shared';
import {
  Wallet,
  Plus,
  ArrowUpRight,
  Store as StoreIcon,
  CheckCircle2,
  AlertTriangle,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  Loader2,
  X,
  Check,
  Trash2,
  ShieldAlert,
  Copy,
} from 'lucide-react';
import { format } from 'date-fns';

interface AssignedStore {
  id: string;
  name: string;
  subdomain: string;
  logo?: string;
  status?: string;
  creditWalletId?: string | null;
}

interface WalletItem {
  id: string;
  accountNumber?: string;
  accountId: string;
  name: string;
  balance: number;
  currency: string;
  lowBalanceThreshold: number;
  isDefault: boolean;
  stores?: AssignedStore[];
  _count?: { transactions: number };
}

interface WalletOverviewResponse {
  activeWallet: WalletItem;
  allWallets: WalletItem[];
  stores: AssignedStore[];
  currentPlan: string;
  transactionFeePercentage: number;
  paybillNumber?: string;
}

interface WalletTransaction {
  id: string;
  walletId: string;
  storeId?: string;
  storeName?: string;
  type: 'TOPUP' | 'FEE_DEDUCTION' | 'BONUS' | 'ADJUSTMENT' | 'REFUND';
  status: string;
  amount: number;
  balanceAfter: number;
  currency: string;
  description: string;
  referenceId?: string;
  paymentMethod?: string;
  createdAt: string;
}

interface LedgerResponse {
  items: WalletTransaction[];
  meta: {
    total: number;
    page: number;
    limit: number;
    pages: number;
    hasMore: boolean;
  };
}

const PRESET_AMOUNTS = [500, 1000, 2500, 5000, 10000];

export default function CreditsWallet() {
  const queryClient = useQueryClient();
  const { user, activeStoreId } = useAuthStore();

  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ALL' | 'TOPUP' | 'FEE_DEDUCTION' | 'BONUS'>('ALL');
  const [currentPage, setCurrentPage] = useState(1);

  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [createWalletModalOpen, setCreateWalletModalOpen] = useState(false);
  const [limitNoticeModalOpen, setLimitNoticeModalOpen] = useState(false);

  const [topUpAmount, setTopUpAmount] = useState<number | ''>(1000);
  const [phoneNumber, setPhoneNumber] = useState(user?.phone || '');
  const [topUpSuccess, setTopUpSuccess] = useState(false);
  const [topUpMode, setTopUpMode] = useState<'STK' | 'PAYBILL'>('STK');

  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>([]);

  const [newWalletName, setNewWalletName] = useState('');
  const [newWalletStores, setNewWalletStores] = useState<string[]>([]);

  const { data: overview, isLoading: isLoadingOverview } = useQuery<WalletOverviewResponse>({
    queryKey: ['billing-wallet-overview', activeStoreId],
    queryFn: () => apiClient.get(API_ENDPOINTS.BILLING.WALLET),
  });

  const allWallets = overview?.allWallets || [];
  const activeWallet = allWallets.find((w) => w.id === (selectedWalletId || overview?.activeWallet?.id)) || overview?.activeWallet || allWallets[0];
  const currentWalletId = activeWallet?.id;

  const totalStores = overview?.stores?.length || 1;
  const totalWallets = allWallets.length || 1;
  const canCreateWallet = totalWallets < totalStores;

  const { data: ledgerData, isLoading: isLoadingLedger } = useQuery<LedgerResponse>({
    queryKey: ['billing-wallet-ledger', currentWalletId, activeTab, currentPage],
    queryFn: () => {
      if (!currentWalletId) return { items: [], meta: { total: 0, page: 1, limit: 20, pages: 1, hasMore: false } };
      const filterType = activeTab === 'ALL' ? '' : `&type=${activeTab}`;
      return apiClient.get(`${API_ENDPOINTS.BILLING.WALLET_TRANSACTIONS}?walletId=${currentWalletId}&page=${currentPage}&limit=15${filterType}`);
    },
    enabled: !!currentWalletId,
  });

  const topUpMutation = useMutation({
    mutationFn: (data: { walletId: string; amount: number; paymentMethod: string; phoneNumber?: string }) =>
      apiClient.post(API_ENDPOINTS.BILLING.WALLET_TOPUP, data),
    onSuccess: () => {
      setTopUpSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['billing-wallet-overview'] });
      queryClient.invalidateQueries({ queryKey: ['billing-wallet-ledger'] });
      setTimeout(() => {
        setTopUpSuccess(false);
        setTopUpModalOpen(false);
      }, 1500);
    },
  });

  const assignStoresMutation = useMutation({
    mutationFn: (data: { walletId: string; storeIds: string[] }) =>
      apiClient.post(API_ENDPOINTS.BILLING.WALLET_ASSIGN_STORES, data),
    onSuccess: () => {
      setAssignModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ['billing-wallet-overview'] });
    },
  });

  const createWalletMutation = useMutation({
    mutationFn: (data: { name: string; storeIds: string[] }) =>
      apiClient.post(API_ENDPOINTS.BILLING.WALLET_CREATE, data),
    onSuccess: (res: any) => {
      setCreateWalletModalOpen(false);
      setNewWalletName('');
      setNewWalletStores([]);
      if (res?.wallet?.id) {
        setSelectedWalletId(res.wallet.id);
      }
      queryClient.invalidateQueries({ queryKey: ['billing-wallet-overview'] });
    },
  });

  const deleteWalletMutation = useMutation({
    mutationFn: (walletId: string) => apiClient.delete(API_ENDPOINTS.BILLING.WALLET_DELETE(walletId)),
    onSuccess: () => {
      setSelectedWalletId(null);
      queryClient.invalidateQueries({ queryKey: ['billing-wallet-overview'] });
    },
  });

  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const formatKES = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const handleCopy = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => {
      setCopiedCode(null);
    }, 2000);
  };

  const handleOpenAssignModal = () => {
    if (!activeWallet || !overview?.stores) return;
    const currentAssigned = overview.stores
      .filter((s) => s.creditWalletId === activeWallet.id)
      .map((s) => s.id);
    setSelectedStoreIds(currentAssigned);
    setAssignModalOpen(true);
  };

  const handleToggleStoreSelection = (storeId: string) => {
    setSelectedStoreIds((prev) =>
      prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
    );
  };

  const handleToggleNewWalletStore = (storeId: string) => {
    setNewWalletStores((prev) =>
      prev.includes(storeId) ? prev.filter((id) => id !== storeId) : [...prev, storeId]
    );
  };

  const handleDeleteWallet = () => {
    if (!activeWallet || activeWallet.isDefault) return;
    if (
      window.confirm(
        `Are you sure you want to delete "${activeWallet.name}"?

Any remaining balance (${formatKES(
          activeWallet.balance
        )}) will be automatically merged into your Primary Credits Wallet, and assigned stores will revert to the primary pool.`
      )
    ) {
      deleteWalletMutation.mutate(activeWallet.id);
    }
  };

  const balance = Number(activeWallet?.balance || 0);
  const threshold = Number(activeWallet?.lowBalanceThreshold || 50);
  const isLowBalance = balance < threshold;
  const isDepleted = balance <= 0;
  const feePercent = (overview?.transactionFeePercentage && overview.transactionFeePercentage > 0)
    ? overview.transactionFeePercentage
    : (PLAN_TRANSACTION_FEES as any)[overview?.currentPlan || user?.plan || 'BASIC'] ?? 6;

  const isOwner =
    user?.role === 'OWNER' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'MERCHANT' ||
    user?.membershipRole === 'OWNER' ||
    user?.membershipRole === 'CO_OWNER';

  if (!isOwner) {
    return (
      <div className="py-16 text-center max-w-md mx-auto space-y-3">
        <div className="w-12 h-12 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center text-amber-700 dark:text-amber-300 mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h2 className="text-base font-bold text-gray-900 dark:text-white">Owner access only</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 max-w-5xl animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#D2D5D9] dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-950/60 flex items-center justify-center text-emerald-800 dark:text-emerald-300">
              <Wallet className="w-4 h-4" />
            </div>
            <h1 className="text-xl font-bold text-[#202223] dark:text-white">Credits wallet</h1>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* New Wallet Button with Capacity Quota */}
          <button
            type="button"
            onClick={() => {
              if (canCreateWallet) {
                setCreateWalletModalOpen(true);
              } else {
                setLimitNoticeModalOpen(true);
              }
            }}
            className={`px-3 py-1.5 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 shadow-2xs transition-colors ${
              canCreateWallet
                ? 'border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-[#202223] dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800'
                : 'border-gray-200 bg-gray-100 text-gray-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-400'
            }`}
            title={`Wallets: ${totalWallets}/${totalStores} stores owned`}
          >
            <Plus className="w-3.5 h-3.5" />
            <span>New Wallet</span>
            <span className="ml-1 px-1.5 py-0.2 rounded text-[10px] font-bold bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300">
              {totalWallets}/{totalStores}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setTopUpModalOpen(true)}
            className="px-4 py-1.5 rounded-lg bg-[#202223] dark:bg-white text-white dark:text-slate-900 text-xs font-semibold hover:opacity-90 transition-opacity inline-flex items-center gap-1.5 shadow-sm"
          >
            <ArrowUpRight className="w-3.5 h-3.5" />
            Top Up Credits
          </button>
        </div>
      </div>

      {/* Multiple Wallets Switcher Bar (if more than 1 wallet exists) */}
      {overview && overview.allWallets.length > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 custom-scrollbar">
          <span className="text-xs font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider shrink-0 mr-1">
            Wallets ({allWallets.length}/{totalStores}):
          </span>
          {overview.allWallets.map((w) => {
            const isSelected = w.id === activeWallet?.id;
            return (
              <button
                key={w.id}
                type="button"
                onClick={() => setSelectedWalletId(w.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all shrink-0 border flex items-center gap-2 ${
                  isSelected
                    ? 'bg-black text-white border-black shadow-sm dark:bg-white dark:text-slate-900'
                    : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300 border-[#D2D5D9] dark:border-slate-700 hover:bg-gray-50'
                }`}
              >
                <span>{w.name}</span>
                {w.accountNumber && (
                  <span className="font-mono text-[10px] font-bold opacity-80 bg-black/10 dark:bg-white/10 px-1 py-0.2 rounded">
                    #{w.accountNumber}
                  </span>
                )}
                <span className={`text-[11px] px-1.5 py-0.2 rounded font-bold ${
                  isSelected ? 'bg-white/20 text-white dark:text-slate-900' : 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white'
                }`}>
                  {formatKES(w.balance)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Low Balance Warning Banner */}
      {isLowBalance && (
        <div className={`p-4 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2 duration-300 ${
          isDepleted
            ? 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-900/60 text-red-900 dark:text-red-200'
            : 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/60 text-amber-900 dark:text-amber-200'
        }`}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
              isDepleted ? 'bg-red-100 dark:bg-red-900/60 text-red-700' : 'bg-amber-100 dark:bg-amber-900/60 text-amber-700'
            }`}>
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-xs font-bold">
                {isDepleted ? 'Credits Depleted' : 'Low Balance'}
              </h4>
              <p className="text-xs opacity-90 mt-0.5 max-w-xl">
                {isDepleted
                  ? 'Balance is 0 — top up to continue.'
                  : `Balance below KES ${threshold.toFixed(0)} threshold.`}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setTopUpAmount(1000);
              setTopUpModalOpen(true);
            }}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold text-white transition-opacity shrink-0 shadow-2xs ${
              isDepleted ? 'bg-red-700 hover:bg-red-800' : 'bg-amber-800 hover:bg-amber-900'
            }`}
          >
            Top Up KES 1,000
          </button>
        </div>
      )}

      {/* Main Balance Overview Card */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-2xl p-6 shadow-sm relative overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
                {activeWallet?.name || 'Primary Credits Wallet'}
              </span>
              {activeWallet?.isDefault && (
                <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300">
                  DEFAULT WALLET
                </span>
              )}
              {activeWallet?.accountNumber && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-800/80 text-emerald-900 dark:text-emerald-200">
                  <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Account No:</span>
                  <span className="text-xs font-black font-mono tracking-widest">{activeWallet.accountNumber}</span>
                  <button
                    type="button"
                    onClick={() => handleCopy(activeWallet.accountNumber!)}
                    className="ml-0.5 p-0.5 hover:bg-emerald-200/60 dark:hover:bg-emerald-800 rounded transition-colors"
                    title="Copy Account Number"
                  >
                    {copiedCode === activeWallet.accountNumber ? (
                      <Check className="w-3 h-3 text-emerald-700 dark:text-emerald-300 stroke-[3]" />
                    ) : (
                      <Copy className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                    )}
                  </button>
                </div>
              )}
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-[11px] font-bold ${
                isDepleted
                  ? 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300'
                  : isLowBalance
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
              }`}>
                <CheckCircle2 className="w-3.5 h-3.5" />
                {isDepleted ? 'DEPLETED' : isLowBalance ? 'LOW BALANCE' : 'HEALTHY BALANCE'}
              </span>
              <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-50 dark:bg-blue-950 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-900">
                {feePercent}% Plan Tx Fee
              </span>
            </div>

            <div>
              <div className="flex items-baseline gap-2">
                <h2 className="text-4xl sm:text-5xl font-black text-[#202223] dark:text-white tracking-tight">
                  {isLoadingOverview ? '...' : formatKES(balance)}
                </h2>
                <span className="text-xs font-bold text-[#5C5F62] dark:text-slate-400">
                  Available Credits
                </span>
              </div>
            </div>

            {/* Prominent M-PESA Paybill Direct Deposit Bar */}
            {activeWallet?.accountNumber && (
              <div className="p-3 bg-gradient-to-r from-emerald-50/90 to-teal-50/90 dark:from-emerald-950/40 dark:to-teal-950/40 border border-emerald-200/90 dark:border-emerald-900/60 rounded-xl flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0">
                    <Smartphone className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-emerald-950 dark:text-emerald-100 flex flex-wrap items-center gap-2">
                      <span>Lipa na M-PESA Paybill:</span>
                      <strong className="font-mono bg-white dark:bg-slate-800 px-1.5 py-0.5 rounded border border-emerald-300 dark:border-slate-700 font-bold">{overview?.paybillNumber || '174379'}</strong>
                      <span>• Account No:</span>
                      <strong className="font-mono text-sm text-emerald-700 dark:text-emerald-300 bg-white dark:bg-slate-800 px-2 py-0.5 rounded border border-emerald-300 dark:border-slate-700 font-black tracking-widest">{activeWallet.accountNumber}</strong>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleCopy(activeWallet.accountNumber!)}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 shrink-0"
                >
                  {copiedCode === activeWallet.accountNumber ? (
                    <>
                      <Check className="w-3.5 h-3.5 stroke-[3]" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      <span>Copy Account No</span>
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Assigned Stores Tag List */}
            <div className="pt-2 border-t border-[#E1E4E8] dark:border-slate-800 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold text-[#5C5F62] dark:text-slate-400 flex items-center gap-1">
                <StoreIcon className="w-3.5 h-3.5 text-gray-400" />
                Assigned Stores:
              </span>
              {(activeWallet?.stores || []).length === 0 ? (
                <span className="text-xs text-gray-400 italic">None</span>
              ) : (
                activeWallet.stores?.map((st) => (
                  <span
                    key={st.id}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-slate-800 text-gray-800 dark:text-slate-200 border border-gray-200 dark:border-slate-700"
                  >
                    {st.name}
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setTopUpModalOpen(true)}
              className="px-5 py-2.5 rounded-xl bg-black dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 shadow-sm"
            >
              <Smartphone className="w-4 h-4" />
              M-PESA Top Up
            </button>
            <button
              type="button"
              onClick={handleOpenAssignModal}
              className="px-5 py-2.5 rounded-xl border border-[#D2D5D9] dark:border-slate-700 bg-white dark:bg-slate-900 text-xs font-semibold text-[#202223] dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
            >
              <StoreIcon className="w-4 h-4 text-gray-500" />
              Assign Stores
            </button>
            {activeWallet && !activeWallet.isDefault && (
              <button
                type="button"
                onClick={handleDeleteWallet}
                disabled={deleteWalletMutation.isPending}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors flex items-center justify-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Wallet
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Wallet Activity Ledger Table */}
      <div className="bg-white dark:bg-slate-900 border border-[#D2D5D9] dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
        {/* Table Filter Tabs */}
        <div className="p-4 border-b border-[#E1E4E8] dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-sm font-bold text-[#202223] dark:text-white">Credits Activity Ledger</h3>
          </div>

          <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 p-1 rounded-xl">
            {(['ALL', 'TOPUP', 'FEE_DEDUCTION', 'BONUS'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === tab
                    ? 'bg-white dark:bg-slate-700 text-black dark:text-white shadow-xs'
                    : 'text-gray-500 dark:text-slate-400 hover:text-black dark:hover:text-white'
                }`}
              >
                {tab === 'ALL' ? 'All' : tab === 'TOPUP' ? 'Top-ups' : tab === 'FEE_DEDUCTION' ? 'Fee Deductions' : 'Bonuses'}
              </button>
            ))}
          </div>
        </div>

        {isLoadingLedger ? (
          <div className="py-16 text-center text-xs text-gray-500">Loading wallet ledger...</div>
        ) : !ledgerData || ledgerData.items.length === 0 ? (
          <div className="py-16 text-center space-y-2">
            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400 mx-auto">
              <Wallet className="w-5 h-5" />
            </div>
            <p className="text-xs font-bold text-gray-700 dark:text-slate-300">No transactions recorded yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-[#E1E4E8] dark:border-slate-800 bg-[#F8F9FA] dark:bg-slate-800/50 text-[11px] font-bold text-[#5C5F62] dark:text-slate-400 uppercase tracking-wider">
                  <th className="px-5 py-3">Date & Time</th>
                  <th className="px-5 py-3">Type</th>
                  <th className="px-5 py-3">Store</th>
                  <th className="px-5 py-3">Description</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3 text-right">Balance After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E1E4E8] dark:divide-slate-800">
                {ledgerData.items.map((tx) => {
                  const isPositive = tx.amount > 0;
                  return (
                    <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="px-5 py-3.5 whitespace-nowrap text-xs text-[#5C5F62] dark:text-slate-400">
                        {format(new Date(tx.createdAt), 'MMM dd, yyyy • HH:mm')}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            tx.type === 'TOPUP'
                              ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300'
                              : tx.type === 'BONUS'
                              ? 'bg-purple-100 dark:bg-purple-950/60 text-purple-800 dark:text-purple-300'
                              : tx.type === 'FEE_DEDUCTION'
                              ? 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300'
                              : 'bg-blue-100 dark:bg-blue-950/60 text-blue-800 dark:text-blue-300'
                          }`}
                        >
                          {tx.type === 'FEE_DEDUCTION' ? 'Fee' : tx.type === 'BONUS' ? 'Bonus' : tx.type === 'TOPUP' ? 'Top Up' : tx.type}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-xs font-semibold text-gray-700 dark:text-slate-300">
                        {tx.storeName ? (
                          <span className="inline-flex items-center gap-1">
                            <StoreIcon className="w-3 h-3 text-gray-400" />
                            {tx.storeName}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-xs font-medium text-[#202223] dark:text-white">
                        {tx.description}
                      </td>
                      <td className={`px-5 py-3.5 whitespace-nowrap text-right text-xs font-bold ${
                        isPositive
                          ? 'text-emerald-700 dark:text-emerald-400'
                          : 'text-[#202223] dark:text-slate-300'
                      }`}>
                        {isPositive ? `+${formatKES(tx.amount)}` : formatKES(tx.amount)}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-right text-xs font-semibold text-[#5C5F62] dark:text-slate-400">
                        {formatKES(tx.balanceAfter)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {ledgerData && ledgerData.meta.pages > 1 && (
          <div className="px-5 py-3 border-t border-[#E1E4E8] dark:border-slate-800 flex items-center justify-between bg-[#F8F9FA] dark:bg-slate-800/40">
            <span className="text-xs text-[#5C5F62] dark:text-slate-400">
              Page {currentPage} of {ledgerData.meta.pages}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                disabled={currentPage >= ledgerData.meta.pages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 1. M-PESA STK Top Up Modal */}
      {topUpModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-xs" onClick={() => setTopUpModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-900 rounded-2xl shadow-2xl space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center text-emerald-700 dark:text-emerald-300">
                    <Smartphone className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Top Up Credits</h3>
                    <p className="text-xs text-gray-500">{activeWallet?.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setTopUpModalOpen(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mode Tabs */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-slate-800 rounded-xl">
                <button
                  type="button"
                  onClick={() => setTopUpMode('STK')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    topUpMode === 'STK'
                      ? 'bg-white dark:bg-slate-700 text-black dark:text-white shadow-xs'
                      : 'text-gray-500 dark:text-slate-400 hover:text-black dark:hover:text-white'
                  }`}
                >
                  <Smartphone className="w-3.5 h-3.5" />
                  Instant STK Push
                </button>
                <button
                  type="button"
                  onClick={() => setTopUpMode('PAYBILL')}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${
                    topUpMode === 'PAYBILL'
                      ? 'bg-white dark:bg-slate-700 text-black dark:text-white shadow-xs'
                      : 'text-gray-500 dark:text-slate-400 hover:text-black dark:hover:text-white'
                  }`}
                >
                  <Wallet className="w-3.5 h-3.5" />
                  Paybill Manual
                </button>
              </div>

              {topUpSuccess ? (
                <div className="py-8 flex flex-col items-center justify-center text-center space-y-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center animate-bounce">
                    <Check className="w-7 h-7 stroke-[3]" />
                  </div>
                  <h4 className="text-base font-bold text-gray-900 dark:text-white">STK Prompt Sent</h4>
                  <p className="text-xs text-gray-500 max-w-xs">
                    Enter M-PESA PIN on {phoneNumber}
                  </p>
                </div>
              ) : topUpMode === 'PAYBILL' ? (
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 dark:bg-slate-800/60 rounded-xl border border-gray-200 dark:border-slate-700 space-y-3">
                    <h4 className="text-xs font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                      <Smartphone className="w-4 h-4 text-emerald-600" />
                      M-PESA Paybill steps:
                    </h4>
                    
                    <ol className="text-xs text-gray-700 dark:text-slate-300 space-y-2.5 list-decimal list-inside">
                      <li>Go to <strong>M-PESA</strong> &gt; <strong>Lipa na M-PESA</strong> &gt; <strong>Paybill</strong></li>
                      
                      <li className="flex flex-wrap items-center justify-between gap-2 p-2 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
                        <span>Business No: <strong className="font-mono font-bold text-black dark:text-white">{overview?.paybillNumber || '174379'}</strong></span>
                        <button
                          type="button"
                          onClick={() => handleCopy(overview?.paybillNumber || '174379')}
                          className="px-2 py-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-300 rounded border border-emerald-200 dark:border-emerald-800 flex items-center gap-1 hover:bg-emerald-100"
                        >
                          {copiedCode === (overview?.paybillNumber || '174379') ? <Check className="w-3 h-3 stroke-[3]" /> : <Copy className="w-3 h-3" />}
                          Copy
                        </button>
                      </li>

                      <li className="flex flex-wrap items-center justify-between gap-2 p-2 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-700">
                        <span>Account No: <strong className="font-mono text-sm font-black text-emerald-700 dark:text-emerald-300 tracking-wider">{activeWallet?.accountNumber || '—'}</strong></span>
                        {activeWallet?.accountNumber && (
                          <button
                            type="button"
                            onClick={() => handleCopy(activeWallet.accountNumber!)}
                            className="px-2 py-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-950 dark:text-emerald-300 rounded border border-emerald-200 dark:border-emerald-800 flex items-center gap-1 hover:bg-emerald-100"
                          >
                            {copiedCode === activeWallet.accountNumber ? <Check className="w-3 h-3 stroke-[3]" /> : <Copy className="w-3 h-3" />}
                            Copy
                          </button>
                        )}
                      </li>

                      <li>Enter <strong>Amount</strong> &amp; <strong>M-PESA PIN</strong>.</li>
                    </ol>
                  </div>

                  <div className="pt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={() => setTopUpModalOpen(false)}
                      className="px-5 py-2 rounded-xl bg-black dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:opacity-90"
                    >
                      Done
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!topUpAmount || Number(topUpAmount) <= 0 || !currentWalletId) return;
                    topUpMutation.mutate({
                      walletId: currentWalletId,
                      amount: Number(topUpAmount),
                      paymentMethod: 'MPESA_STK',
                      phoneNumber,
                    });
                  }}
                  className="space-y-4"
                >
                  <div>
                    <label className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider block mb-1.5">
                      Select Amount (KES)
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      {PRESET_AMOUNTS.map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setTopUpAmount(amt)}
                          className={`py-2 rounded-lg text-xs font-bold border transition-all ${
                            topUpAmount === amt
                              ? 'bg-black text-white border-black dark:bg-white dark:text-slate-900 shadow-sm'
                              : 'bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-200 border-gray-200 dark:border-slate-700 hover:border-gray-400'
                          }`}
                        >
                          +{amt.toLocaleString()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider block mb-1">
                      Custom Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                        KES
                      </span>
                      <input
                        type="number"
                        min="50"
                        step="50"
                        value={topUpAmount}
                        onChange={(e) => setTopUpAmount(e.target.value ? Number(e.target.value) : '')}
                        placeholder="1000"
                        className="w-full h-10 pl-12 pr-3 rounded-lg border border-gray-300 dark:border-slate-700 bg-transparent text-sm font-bold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider block mb-1">
                      M-PESA Phone Number
                    </label>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="0712345678 or 254712345678"
                      className="w-full h-10 px-3 rounded-lg border border-gray-300 dark:border-slate-700 bg-transparent text-xs font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black"
                      required
                    />
                  </div>

                  <div className="pt-2 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setTopUpModalOpen(false)}
                      className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:text-black"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={topUpMutation.isPending || !topUpAmount || Number(topUpAmount) <= 0}
                      className="px-5 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 transition-colors flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                    >
                      {topUpMutation.isPending ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Sending STK Prompt...
                        </>
                      ) : (
                        <>Pay {topUpAmount ? formatKES(Number(topUpAmount)) : ''}</>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 2. Assign Stores Modal (Exclusive Assignment) */}
      {assignModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-xs" onClick={() => setAssignModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-900 rounded-2xl shadow-2xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Assign Stores</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setAssignModalOpen(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-950/40 rounded-xl border border-blue-100 dark:border-blue-900/60 text-[11px] text-blue-900 dark:text-blue-200">
                One wallet per store.
              </div>

              <div className="space-y-2 max-h-60 overflow-y-auto custom-scrollbar">
                {(overview?.stores || []).map((store) => {
                  const isSelected = selectedStoreIds.includes(store.id);
                  const currentWallet = allWallets.find((w) => w.id === store.creditWalletId);
                  const isAssignedToOther = store.creditWalletId && store.creditWalletId !== activeWallet?.id;

                  return (
                    <div
                      key={store.id}
                      onClick={() => handleToggleStoreSelection(store.id)}
                      className={`p-3 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                        isSelected
                          ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-500'
                          : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-gray-600">
                          <StoreIcon className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-xs font-bold text-gray-900 dark:text-white">{store.name}</p>
                            {isAssignedToOther && (
                              <span className="text-[10px] font-semibold px-1.5 py-0.2 rounded bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                                Currently on: {currentWallet?.name || 'Other Wallet'}
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-gray-500">{store.subdomain}.dukene.com</p>
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 dark:border-slate-600'
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAssignModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:text-black dark:text-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={assignStoresMutation.isPending}
                  onClick={() => {
                    if (!currentWalletId) return;
                    assignStoresMutation.mutate({
                      walletId: currentWalletId,
                      storeIds: selectedStoreIds,
                    });
                  }}
                  className="px-5 py-2 rounded-xl bg-black dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:opacity-90 transition-opacity flex items-center gap-1.5"
                >
                  {assignStoresMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3. Create Wallet Modal */}
      {createWalletModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-xs" onClick={() => setCreateWalletModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-900 rounded-2xl shadow-2xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Create New Credits Wallet</h3>
                  <p className="text-xs text-gray-500">
                    Wallet {totalWallets + 1} of {totalStores} allowed
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCreateWalletModalOpen(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!newWalletName.trim()) return;
                  createWalletMutation.mutate({
                    name: newWalletName.trim(),
                    storeIds: newWalletStores,
                  });
                }}
                className="space-y-4"
              >
                <div>
                  <label className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider block mb-1">
                    Wallet Name
                  </label>
                  <input
                    type="text"
                    value={newWalletName}
                    onChange={(e) => setNewWalletName(e.target.value)}
                    placeholder="e.g. Nairobi Outlet"
                    className="w-full h-10 px-3 rounded-lg border border-gray-300 dark:border-slate-700 bg-transparent text-xs font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-black"
                    required
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-gray-700 dark:text-slate-300 uppercase tracking-wider block mb-1">
                    Assign Stores (Optional)
                  </label>
                  <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                    {(overview?.stores || []).map((store) => {
                      const isSelected = newWalletStores.includes(store.id);
                      const currentWallet = allWallets.find((w) => w.id === store.creditWalletId);

                      return (
                        <div
                          key={store.id}
                          onClick={() => handleToggleNewWalletStore(store.id)}
                          className={`p-2.5 rounded-xl border flex items-center justify-between cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-500'
                              : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <StoreIcon className="w-4 h-4 text-gray-500" />
                            <div>
                              <p className="text-xs font-bold text-gray-900 dark:text-white">{store.name}</p>
                              {store.creditWalletId && (
                                <p className="text-[10px] text-gray-400">
                                  Currently on: {currentWallet?.name || 'Another wallet'}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            isSelected ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-gray-300 dark:border-slate-600'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-2 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setCreateWalletModalOpen(false)}
                    className="px-4 py-2 text-xs font-semibold text-gray-600 dark:text-slate-400 hover:text-black"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createWalletMutation.isPending || !newWalletName.trim()}
                    className="px-5 py-2 rounded-xl bg-black dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:opacity-90 transition-opacity flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {createWalletMutation.isPending ? 'Creating...' : 'Create Wallet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* 4. Wallet Limit Notice Modal */}
      {limitNoticeModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 text-center">
            <div className="fixed inset-0 transition-opacity bg-black/60 backdrop-blur-xs" onClick={() => setLimitNoticeModalOpen(false)} />
            
            <div className="relative inline-block w-full max-w-md p-6 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-slate-900 rounded-2xl shadow-2xl space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-950 flex items-center justify-center text-amber-700 dark:text-amber-300 shrink-0">
                  <ShieldAlert className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Wallet Limit Reached</h3>
                  <p className="text-xs text-gray-500">
                    {totalWallets}/{totalStores} wallets created
                  </p>
                </div>
              </div>

              <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed">
                One wallet per store — you've used all {totalStores}.
              </p>

              <div className="p-3.5 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 text-xs text-gray-600 dark:text-slate-300">
                <p className="text-[11px] text-gray-500">
                  Add a new store to unlock another wallet.
                </p>
              </div>

              <div className="pt-2 flex items-center justify-end">
                <button
                  type="button"
                  onClick={() => setLimitNoticeModalOpen(false)}
                  className="px-5 py-2 rounded-xl bg-black dark:bg-white text-white dark:text-slate-900 text-xs font-bold hover:opacity-90"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
